import { Box, Grid, GridItem } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import { bvServiceLocation, woundTypeMap } from '@/constants';
import { useGetPhysicians } from '@/hooks/service-hooks/contactsAccounts';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { Physician } from '@/services/contactsAccounts';
import { useClinicStore } from '@/store';
import type { FormMethods } from '@/types/form';
export interface FacilityInformationProps {
	formMethods: FormMethods<BVFormData>;
}

const statusItems: SelectItem[] = [
	{ value: 'high-priority', label: 'High Priority' },
	{ value: 'medium-priority', label: 'Medium Priority' },
	{ value: 'low-priority', label: 'Low Priority' },
	{ value: 'urgent', label: 'Urgent' },
];

const FacilityInformation: React.FC<FacilityInformationProps> = ({
	// data,
	// onChange,
	// errors
	formMethods,
}) => {
	const { watch, errors, setValue, setError } = formMethods;

	const accounts = useClinicStore((state) =>
		state.currentClinics,
	).map((clinic) => ({
			label: clinic.name,
			value: clinic.accountid,
	}));

	// Physicians with pagination
	const physicians = usePaginatedSelect({
		queryHook: useGetPhysicians,
		queryParams: {
			clinic_id: (watch('clinic') as SelectItem)?.value || undefined,
			enabled: !!watch('clinic'),
		},
		transform: (physician: Physician) => ({
			value: physician.providerId,
			label: physician.providername,
		}),
		errorMessage: 'Unable to load providers',
		resetKey: ((watch('clinic') as SelectItem)?.value as string) || '',
	});

	return (
		<Box>
			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Facility Information
			</FaceliftText>

			<Grid
				templateColumns={{
					base: '1fr',
					sm: 'repeat(1, 1fr)',
					md: 'repeat(2, 1fr)',
				}}
				gap={{ base: 4, sm: 5, md: 6 }}
				alignItems="end"
			>
				{/* Select Clinic */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Select Clinic"
						required
						value={
							accounts.find(
								(item) => item.value === (watch('clinic') as SelectItem)?.value,
							) || null
						}
						error={errors.clinic?.message}
					onChange={(newValue: SingleValue<SelectItem>) => {
						const currentValues = watch();
						setValue('clinic', (newValue as SelectItem) || (undefined as unknown as SelectItem), { shouldDirty: true });
						setValue('provider', undefined as unknown as SelectItem, { shouldDirty: true });
						setValue('patientFirstName', '', { shouldDirty: true });
						setValue('patientLastName', '', { shouldDirty: true });
						setValue('physicianFirstName', '', { shouldDirty: true });
						setValue('physicianLastName', '', { shouldDirty: true });
						setValue('patient', undefined as unknown as SelectItem, { shouldDirty: true });
						setValue('birthdate', '', { shouldDirty: true });
						setValue('primaryInsurance', undefined as unknown as SelectItem, { shouldDirty: true });
						setValue('ta_primarymemberid', '', { shouldDirty: true });
						setValue('ta_secondarymemberid', '', { shouldDirty: true });
						setValue('secondaryInsurance', undefined as unknown as SelectItem, { shouldDirty: true });
						setValue('ta_emrid', '', { shouldDirty: true });
						setValue('totalWoundsCreated', currentValues.totalWoundsCreated || 1, { shouldDirty: false });
					}}
						// onChange('selectClinic', (newValue?.value as string) || '')
						items={accounts}
					/>
				</GridItem>

				{/* Provider */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Provider"
						value={watch('provider') as SelectItem | null}
						required
					onChange={(newValue: SingleValue<SelectItem>) => {
						setValue('provider', (newValue as SelectItem) || '', { shouldDirty: true });
						setValue(
							'physicianFirstName',
							(newValue as SelectItem).label?.split(' ').at(0) || '',
							{ shouldDirty: true },
						);
						setValue(
							'physicianLastName',
							(newValue as SelectItem).label?.split(' ').at(-1) || '',
							{ shouldDirty: true },
						);
						setError('provider', { type: 'manual', message: '' });
					}}
						items={physicians.items}
						loading={physicians.loading}
						isDisabled={!watch('clinic')}
						error={errors?.provider?.message}
						onLoadMore={physicians.onLoadMore}
					/>
				</GridItem>

				<GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={15}
						topLabel="Service Location"
						required
						value={
							bvServiceLocation.find(
								(item) =>
									item.value === (watch('ta_servicelocation') as SelectItem)?.value,
							) || null
						}
						onChange={(newValue: SingleValue<SelectItem>) =>
							setValue('ta_servicelocation', newValue as SelectItem, { shouldDirty: true })
						}
						items={bvServiceLocation}
						placeholder="Select Service Location"
						error={errors.ta_servicelocation?.message}
					/>
				</GridItem>
				{/* Status */}
				{/* <GridItem>
					<FaceliftSelect<SelectItem, false>
						limit={10}
						topLabel="Status"
						// value={
						// 	statusItems.find((item) => item.value === data.status) || null
						// }
						onChange={(newValue: SingleValue<SelectItem>) => { }}
						// onChange('status', (newValue?.value as string) || '')
						items={statusItems}
					/>
				</GridItem> */}
			</Grid>
		</Box>
	);
};

export default FacilityInformation;
